import type { PixelCrop } from "react-image-crop";

function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (err) => reject(err));
    image.src = url;
  });
}

/**
 * Crops the visible `<img>` region described by `pixelCrop` (display pixels)
 * into a JPEG blob using natural dimensions for sharp output.
 */
export async function getCroppedImageBlobFromPixelCrop(
  image: HTMLImageElement,
  pixelCrop: PixelCrop,
  quality = 0.92,
): Promise<Blob> {
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  const x = pixelCrop.x * scaleX;
  const y = pixelCrop.y * scaleY;
  const w = pixelCrop.width * scaleX;
  const h = pixelCrop.height * scaleY;

  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.floor(w));
  canvas.height = Math.max(1, Math.floor(h));
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Could not get canvas context");
  }
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(image, x, y, w, h, 0, 0, w, h);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Canvas toBlob failed"));
      },
      "image/jpeg",
      quality,
    );
  });
}

/** Rotate image by ±90° and return a new JPEG object URL (caller owns revoke). */
export async function rotateImageToBlobUrl(
  imageSrc: string,
  degreesClockwise: 90 | -90,
  quality = 0.95,
): Promise<string> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Could not get canvas context");
  }

  canvas.width = image.naturalHeight;
  canvas.height = image.naturalWidth;
  const rad = (degreesClockwise * Math.PI) / 180;
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate(rad);
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(image, -image.naturalWidth / 2, -image.naturalHeight / 2);

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => {
        if (b) resolve(b);
        else reject(new Error("rotate toBlob failed"));
      },
      "image/jpeg",
      quality,
    );
  });
  return URL.createObjectURL(blob);
}

/** Downscale so longest edge is at most `maxEdge` (keeps aspect ratio). */
export async function downscaleBlobIfNeeded(
  blob: Blob,
  maxEdge: number,
): Promise<Blob> {
  const url = URL.createObjectURL(blob);
  try {
    const image = await createImage(url);
    const w = image.width;
    const h = image.height;
    const longest = Math.max(w, h);
    if (longest <= maxEdge) return blob;

    const scale = maxEdge / longest;
    const tw = Math.round(w * scale);
    const th = Math.round(h * scale);
    const canvas = document.createElement("canvas");
    canvas.width = tw;
    canvas.height = th;
    const ctx = canvas.getContext("2d");
    if (!ctx) return blob;
    ctx.drawImage(image, 0, 0, tw, th);
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (b) => {
          if (b) resolve(b);
          else reject(new Error("downscale toBlob failed"));
        },
        "image/jpeg",
        0.9,
      );
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}
